import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Calendar, User, Activity, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  'hierarchy_change': 'Hierarchy Change',
  'status_change': 'Status Change',
  'commission_change': 'Commission Change',
  'role_change': 'Role Change'
};

const ACTION_COLORS = {
  'hierarchy_change': 'blue',
  'status_change': 'emerald',
  'commission_change': 'violet',
  'role_change': 'amber'
};

export default function AuditLog() {
  const { getAuthHeader } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAuditLogs();
  }, [filter]);

  const fetchAuditLogs = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/admin/audit-logs` 
        : `${API}/admin/audit-logs?action=${filter}`;
      const response = await axios.get(url, getAuthHeader());
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderDetails = (action, details) => {
    switch (action) {
      case 'hierarchy_change':
        return (
          <div className="text-xs text-[#A3AED0] space-y-0.5">
            <div>From: {details.old_upline_name || 'None'} → To: {details.new_upline_name || 'None'}</div>
          </div>
        );
      case 'status_change':
        return (
          <div className="text-xs text-[#A3AED0]">
            {details.old_status} → {details.new_status}
          </div>
        );
      case 'commission_change':
        return (
          <div className="text-xs text-[#A3AED0]">
            {details.old_commission}% → {details.new_commission}%
          </div>
        );
      case 'role_change':
        return (
          <div className="text-xs text-[#A3AED0]">
            {details.old_role} → {details.new_role}
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#2447D6] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="audit-log-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-3">
        
        <div>
          <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-[#2B3674]">Audit Log</h1>
          <p className="text-xs text-[#A3AED0]">Track all administrative actions</p>
        </div>
      </motion.div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'hierarchy_change', 'status_change', 'commission_change', 'role_change'].map((actionType) => (
          <button
            key={actionType}
            onClick={() => setFilter(actionType)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
              filter === actionType
                ? 'bg-[#2447D6] text-white shadow-md'
                : 'bg-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-200'
            }`}
            data-testid={`filter-${actionType}`}
          >
            {actionType === 'all' ? 'All' : ACTION_LABELS[actionType]}
          </button>
        ))}
      </div>

      {/* Logs */}
      <Card className="bg-white">
        <CardContent className="p-0">
          {logs.length > 0 ? (
            <div className="divide-y divide-[#E9EDF7]">
              {logs.map((log, index) => {
                const color = ACTION_COLORS[log.action] || 'slate';
                return (
                  <motion.div
                    key={log.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 hover:bg-[#F4F7FE] transition-colors"
                    data-testid={`audit-log-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-${color}-50 flex-shrink-0`}>
                        <Activity className={`h-4 w-4 text-${color}-600`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full bg-${color}-100 text-${color}-700 font-medium`}>
                            {ACTION_LABELS[log.action]}
                          </span>
                          <span className="text-xs text-[#A3AED0]">•</span>
                          <span className="text-xs text-[#A3AED0]">{formatDate(log.created_at)}</span>
                        </div>
                        <div className="text-sm font-medium text-[#2B3674] mb-1">
                          {log.target_user_name}
                        </div>
                        {renderDetails(log.action, log.details)}
                        <div className="text-xs text-[#A3AED0] mt-1.5 flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>by {log.admin_name}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <FileText className="h-12 w-12 mx-auto text-[#A3AED0] mb-3" />
              <p className="text-[#A3AED0] text-sm">No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
